import { useState, type FormEvent, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/platformAuthStore';
import { theme } from '../theme';

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 56px)',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    background: theme.bgPanel,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.border}`,
    padding: '32px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '24px',
    textAlign: 'center' as const,
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    color: theme.textSecondary,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.textPrimary,
    fontSize: '14px',
    outline: 'none',
  },
  btn: {
    width: '100%',
    padding: '10px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 600,
    background: theme.accent,
    color: theme.accentText,
    marginTop: '8px',
  },
  error: {
    color: theme.error,
    fontSize: '13px',
    marginBottom: '12px',
    textAlign: 'center' as const,
  },
  footer: {
    textAlign: 'center' as const,
    marginTop: '16px',
    fontSize: '13px',
    color: theme.textMuted,
  },
  link: {
    color: theme.accent,
    textDecoration: 'none',
  },
  check: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: theme.textSecondary,
    marginBottom: '10px',
    cursor: 'pointer',
  },
};

export function signupPasswordError(password: string, confirm: string): string | null {
  return password === confirm ? null : 'The two passwords do not match';
}

export function Signup() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState('');
  const [confirmAge, setConfirmAge] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [params] = useSearchParams();
  const [inviteCode, setInviteCode] = useState(params.get('invite') ?? '');
  const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null);
  const { signup, isLoading, error, token, clearError } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) navigate('/dashboard', { replace: true });
  }, [token, navigate]);

  // Checked separately from the signup attempt itself so the closed state
  // shows up front rather than only after a submit that was always going to
  // 403 - the server is still the real enforcement point either way.
  useEffect(() => {
    fetch('/api/platform/config')
      .then((r) => (r.ok ? r.json() : { signupEnabled: true }))
      .then((cfg) => setSignupEnabled(cfg.signupEnabled !== false))
      .catch(() => setSignupEnabled(true));
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    clearError();
    const problem = signupPasswordError(password, confirmPassword);
    setMismatch(problem ?? '');
    if (problem) return;
    signup(email, username, password, acceptTerms, confirmAge, inviteCode);
  };

  if (signupEnabled === false) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Signups are closed</h2>
          <p style={{ ...styles.footer, marginTop: 0 }}>
            This server is not accepting new accounts right now. Ask whoever runs it for an invite.
          </p>
          <div style={styles.footer}>
            <Link to="/login" style={styles.link}>
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <form style={styles.card} onSubmit={handleSubmit}>
        <h2 style={styles.title}>Create Account</h2>

        {(mismatch || error) && <div style={styles.error}>{mismatch || error}</div>}

        <div style={styles.field}>
          <label style={styles.label}>Invite code</label>
          <input
            style={styles.input}
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder="LUNE-XXXX-XXXX-XXXX"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Username</label>
          <input
            style={styles.input}
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            minLength={3}
            maxLength={30}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Confirm password</label>
          <input
            style={styles.input}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>

        <label style={styles.check}>
          <input
            type="checkbox"
            checked={confirmAge}
            onChange={(e) => setConfirmAge(e.target.checked)}
            required
          />
          I am 18 or older
        </label>

        <label style={styles.check}>
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            required
          />
          <span>
            I agree to the{' '}
            <a
              href="https://luneorpg.com/terms"
              target="_blank"
              rel="noreferrer"
              style={styles.link}
            >
              beta terms
            </a>
          </span>
        </label>

        <button style={styles.btn} type="submit" disabled={isLoading}>
          {isLoading ? 'Creating account...' : 'Create Account'}
        </button>

        <div style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>
            Log in
          </Link>
        </div>
      </form>
    </div>
  );
}
